import { useEffect, useState } from 'react';
import { getIskDisplayImage } from '@kelilearning/psycheflow-contracts';

import { assetService } from '../../services/assetService';
import { buildAssetPreviewUrl } from '../../utils/mediaUrl';

/** 列表缩略：OSS 服务端缩放 */
export const ISK_THUMB_OSS_PROCESS = 'image/resize,m_lfit,w_320,h_320/quality,q_72';
export const ISK_THUMB_DISPLAY_PX = 160;

function extractOssKeyFromUrl(rawUrl?: string | null): string {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    const key = parsed.pathname.replace(/^\/+/, '').trim();
    if (key.startsWith('assets/') || key.startsWith('ttq/')) return key;
  } catch {
    // ignore
  }
  return '';
}

function toHttps(url: string): string {
  return url.replace(/^http:\/\//i, 'https://');
}

function withOssProcess(rawUrl: string, process: string): string {
  try {
    const parsed = new URL(rawUrl.startsWith('//') ? `https:${rawUrl}` : rawUrl);
    parsed.searchParams.set('x-oss-process', process);
    return parsed.toString();
  } catch {
    const sep = rawUrl.includes('?') ? '&' : '?';
    return `${rawUrl}${sep}x-oss-process=${encodeURIComponent(process)}`;
  }
}

type IskToolbarThumbProps = {
  imageUrl?: string | null;
  imageOssId?: string | null;
  alt?: string;
  size?: number;
  /** 铺满父容器（卡片缩略区） */
  fill?: boolean;
  className?: string;
};

/**
 * 品题直觉草图缩略图（加载中也占位，避免右侧空白）。
 */
export function IskToolbarThumb({
  imageUrl,
  imageOssId,
  alt = '直觉草图',
  size = ISK_THUMB_DISPLAY_PX,
  fill = false,
  className = '',
}: IskToolbarThumbProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const side = Math.max(40, Math.round(size));
  const hasSource = Boolean(String(imageOssId || '').trim() || String(imageUrl || '').trim());

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    setFailed(false);
    if (!hasSource) return undefined;

    const boot = async () => {
      const ossId = String(imageOssId || '').trim() || extractOssKeyFromUrl(imageUrl);
      if (ossId) {
        try {
          const signed = toHttps(await assetService.getSignedOssUrl(ossId, {
            process: ISK_THUMB_OSS_PROCESS,
          }));
          if (!cancelled && signed) {
            setSrc(signed);
            return;
          }
        } catch {
          // fall through
        }
        try {
          const signedPlain = toHttps(await assetService.getSignedOssUrl(ossId));
          if (!cancelled && signedPlain) {
            setSrc(signedPlain);
            return;
          }
        } catch {
          // fall through
        }
      }

      const raw = buildAssetPreviewUrl(imageUrl) || String(imageUrl || '').trim();
      if (!cancelled && raw) {
        setSrc(withOssProcess(toHttps(raw), ISK_THUMB_OSS_PROCESS));
      } else if (!cancelled) {
        setFailed(true);
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, [hasSource, imageOssId, imageUrl]);

  if (!hasSource) return null;

  return (
    <span
      className={
        `inline-flex items-center justify-center overflow-hidden rounded-md border border-border/70 bg-[#EDF1F7] `
        + (fill ? 'h-full w-full min-h-0 min-w-0 ' : 'shrink-0 ')
        + className
      }
      style={fill ? undefined : { width: side, height: side }}
    >
      {!failed && src ? (
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => {
            setFailed(true);
            setSrc(null);
          }}
        />
      ) : (
        <span className="text-[10px] text-muted">{failed ? '无图' : '…'}</span>
      )}
    </span>
  );
}

export function ipttqHasIskPreview(record: {
  skill?: { intuitionSketch?: Parameters<typeof getIskDisplayImage>[0] };
}): boolean {
  const sketch = record.skill?.intuitionSketch;
  if (!sketch) return false;
  const display = getIskDisplayImage(sketch);
  if (!display) return false;
  const status = display.status || sketch.status;
  if (status && status !== 'ready' && status !== 'failed') return false;
  return Boolean(String(display.imageUrl || '').trim() || String(display.imageOssId || '').trim());
}

/** 从浓缩结果记录抽出列表预览（最多 3）——用选用槽 */
export function collectIskPreviewsFromRecords(
  records: Array<{
    isDefault?: boolean;
    skill?: { intuitionSketch?: Parameters<typeof getIskDisplayImage>[0] };
  }> | null | undefined,
): Array<{ imageUrl: string | null; imageOssId: string | null }> {
  const ordered = [...(records || [])].sort((left, right) => {
    if (Boolean(left.isDefault) === Boolean(right.isDefault)) return 0;
    return left.isDefault ? -1 : 1;
  });
  const out: Array<{ imageUrl: string | null; imageOssId: string | null }> = [];
  for (const record of ordered) {
    if (!ipttqHasIskPreview(record)) continue;
    const display = getIskDisplayImage(record.skill?.intuitionSketch);
    out.push({
      imageUrl: String(display?.imageUrl || '').trim() || null,
      imageOssId: String(display?.imageOssId || '').trim() || null,
    });
    if (out.length >= 3) break;
  }
  return out;
}
